"use client";

import { motion, type Variants } from "framer-motion";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Globe2,
  LifeBuoy,
  Palette,
  Search,
  Share2,
  Smartphone,
} from "lucide-react";

const iconMap = {
  Search,
  Smartphone,
  Globe2,
  Palette,
  LifeBuoy,
  Share2,
} as const;

type IconName = keyof typeof iconMap;

export type Problem = {
  title: string;
  description: string;
  icon?: IconName;
};

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" as const },
  },
};

export function ProblemSection({ problems }: { problems: Problem[] }) {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className="grid gap-6 md:grid-cols-3"
    >
      {problems.map((problem, index) => {
        const Icon = problem.icon ? iconMap[problem.icon] : null;

        return (
          <motion.div key={problem.title} variants={itemVariants}>
            <Card
              className="
                group relative h-full overflow-hidden
                rounded-3xl border border-white/10
                bg-white/5 p-6
                backdrop-blur-xl
                transition-all duration-300
                hover:-translate-y-1 hover:border-white/20 hover:bg-white/10
              "
            >
              {/* gradient glow */}
              <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <div className="absolute -inset-px rounded-3xl bg-linear-to-br from-[#35A29F]/15 via-transparent to-transparent" />
              </div>

              <CardHeader className="relative z-10 space-y-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-lg font-semibold text-[#F2F7A1]">
                  {Icon ? (
                    <Icon className="h-5 w-5" />
                  ) : (
                    String(index + 1).padStart(2, "0")
                  )}
                </div>

                <CardTitle className="text-lg font-semibold leading-snug text-white">
                  {problem.title}
                </CardTitle>

                <CardDescription className="text-sm leading-relaxed text-white/75">
                  {problem.description}
                </CardDescription>
              </CardHeader>
            </Card>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
